import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Chip,
  FormControlLabel,
  IconButton,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { listLogs, exportLogsCsv } from '../../api/admin';
import { ApiError } from '../../api/client';
import { useAuth } from '../../auth/useAuth';
import type { LogCategory, LogEntry } from '../../api/types';
import DataTable, { type DataTableColumn, type DataTableFilter } from '../../components/DataTable';

const ALL_CATEGORIES: LogCategory[] = ['audit', 'app', 'error', 'access'];
// Backend caps the list `limit` query param at 200.
const FETCH_LIMIT = 200;

const CATEGORY_COLORS: Record<LogCategory, 'default' | 'primary' | 'error' | 'warning'> = {
  audit: 'primary',
  app: 'default',
  error: 'error',
  access: 'warning',
};

const LogViewer: React.FC = () => {
  const { t, i18n } = useTranslation('admin');
  const { user } = useAuth();
  const orgSlug = user?.organization_slug ?? '';

  const [entries, setEntries] = useState<LogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  // Server-side date-range filters (bound volume); applied on demand.
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [appliedFromDate, setAppliedFromDate] = useState('');
  const [appliedToDate, setAppliedToDate] = useState('');

  // Auto-refresh
  const [autoRefresh, setAutoRefresh] = useState(false);

  const load = useCallback(async () => {
    if (!orgSlug) return;
    setLoading(true);
    setError(null);
    try {
      const data = await listLogs(orgSlug, {
        from_date: appliedFromDate || undefined,
        to_date: appliedToDate || undefined,
        limit: FETCH_LIMIT,
      });
      setEntries(data.items);
    } catch (err) {
      setError(err instanceof ApiError ? err.detail : String(err));
    } finally {
      setLoading(false);
    }
  }, [orgSlug, appliedFromDate, appliedToDate]);

  useEffect(() => {
    void load();
  }, [load]);

  // Auto-refresh polling — setInterval with cleanup; loadRef avoids re-arming on every load change.
  const loadRef = useRef(load);
  useEffect(() => {
    loadRef.current = load;
  }, [load]);

  useEffect(() => {
    if (!autoRefresh) return;
    const id = setInterval(() => {
      void loadRef.current();
    }, 10000);
    return () => clearInterval(id);
  }, [autoRefresh]);

  const handleApplyFilters = () => {
    setAppliedFromDate(fromDate);
    setAppliedToDate(toDate);
  };

  const handleResetFilters = () => {
    setFromDate('');
    setToDate('');
    setAppliedFromDate('');
    setAppliedToDate('');
  };

  const handleExportCsv = async () => {
    try {
      const csv = await exportLogsCsv(orgSlug, {
        from_date: appliedFromDate || undefined,
        to_date: appliedToDate || undefined,
      });
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `audit-logs-${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(err instanceof ApiError ? err.detail : String(err));
    }
  };

  const formatTimestamp = (ts: string | null) => {
    if (!ts) return '—';
    return new Date(ts).toLocaleString(i18n.language);
  };

  const formatActor = (actor: Record<string, unknown> | null) => {
    if (!actor) return '—';
    const name = actor.display_name ?? actor.email;
    if (name) return String(name);
    const id = actor.user_id ?? actor.id;
    const role = actor.role;
    if (id && role) return `${String(role)} (${String(id).slice(0, 8)}…)`;
    if (id) return `${String(id).slice(0, 8)}…`;
    if (role) return String(role);
    return '—';
  };

  const formatTarget = (target: Record<string, unknown> | null) => {
    if (!target) return '—';
    const label = target.type ?? target.id ?? JSON.stringify(target);
    return String(label);
  };

  const rowId = (entry: LogEntry) => entry.request_id ?? `${entry.timestamp ?? ''}-${entry.event ?? ''}`;

  const toggleExpanded = (id: string) =>
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const columns: DataTableColumn<LogEntry>[] = [
    {
      id: 'timestamp',
      header: t('logs.table.timestamp'),
      render: (e) => {
        const id = rowId(e);
        const hasDetails = e.changes !== null || e.integrity_hash !== null;
        const expanded = expandedRows.has(id);
        return (
          <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center', whiteSpace: 'nowrap' }}>
            {hasDetails && (
              <IconButton
                size="small"
                aria-label={expanded ? t('logs.collapse') : t('logs.expand')}
                aria-expanded={expanded}
                onClick={(ev) => {
                  ev.stopPropagation();
                  toggleExpanded(id);
                }}
              >
                {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
              </IconButton>
            )}
            <span>{formatTimestamp(e.timestamp)}</span>
          </Stack>
        );
      },
      sortValue: (e) => e.timestamp ?? '',
    },
    {
      id: 'category',
      header: t('logs.table.category'),
      render: (e) =>
        e.category ? (
          <Chip
            label={t(`logs.categories.${e.category}`)}
            color={CATEGORY_COLORS[e.category as LogCategory] ?? 'default'}
            size="small"
          />
        ) : null,
      sortValue: (e) => e.category ?? '',
    },
    {
      id: 'event',
      header: t('logs.table.event'),
      render: (e) => (
        <Box component="span" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
          {e.event ?? '—'}
        </Box>
      ),
      searchValue: (e) => e.event ?? '',
    },
    {
      id: 'actor',
      header: t('logs.table.actor'),
      render: (e) => formatActor(e.actor),
      searchValue: (e) => formatActor(e.actor),
    },
    {
      id: 'target',
      header: t('logs.table.target'),
      render: (e) => formatTarget(e.target),
      searchValue: (e) => formatTarget(e.target),
    },
    {
      id: 'requestId',
      header: t('logs.table.requestId'),
      hideBelow: 'md',
      render: (e) => (
        <Box component="span" sx={{ fontFamily: 'monospace', fontSize: '0.75rem', opacity: 0.7 }}>
          {e.request_id ? e.request_id.slice(0, 8) + '…' : '—'}
        </Box>
      ),
      searchValue: (e) => e.request_id ?? '',
    },
  ];

  const filters: DataTableFilter<LogEntry>[] = [
    {
      id: 'category',
      label: t('logs.filters.category'),
      options: ALL_CATEGORIES.map((cat) => ({ value: cat, label: t(`logs.categories.${cat}`) })),
      match: (e, value) => e.category === value,
    },
  ];

  const extraFilters = (
    <>
      <TextField
        label={t('logs.filters.fromDate')}
        type="datetime-local"
        value={fromDate}
        onChange={(e) => setFromDate(e.target.value)}
        size="small"
        slotProps={{ inputLabel: { shrink: true } }}
        sx={{ minWidth: 200 }}
      />
      <TextField
        label={t('logs.filters.toDate')}
        type="datetime-local"
        value={toDate}
        onChange={(e) => setToDate(e.target.value)}
        size="small"
        slotProps={{ inputLabel: { shrink: true } }}
        sx={{ minWidth: 200 }}
      />
      <Button variant="contained" size="small" onClick={handleApplyFilters}>
        {t('logs.filters.apply')}
      </Button>
      <Button variant="outlined" size="small" onClick={handleResetFilters}>
        {t('logs.filters.reset')}
      </Button>
      <FormControlLabel
        control={
          <Switch
            checked={autoRefresh}
            onChange={(e) => setAutoRefresh(e.target.checked)}
            size="small"
          />
        }
        label={t('logs.autoRefresh')}
        sx={{ ml: 0 }}
      />
    </>
  );

  const toolbarActions = (
    <Tooltip title={t('logs.export')}>
      <Button
        variant="outlined"
        startIcon={<DownloadIcon />}
        onClick={() => void handleExportCsv()}
        size="small"
      >
        {t('logs.export')}
      </Button>
    </Tooltip>
  );

  const renderExpanded = (entry: LogEntry) => (
    <Box
      sx={{
        p: 2,
        bgcolor: 'grey.50',
        borderBottom: 1,
        borderColor: 'divider',
      }}
    >
      {entry.changes !== null && (
        <Box sx={{ mb: 1 }}>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            Changes
          </Typography>
          <Box
            component="pre"
            sx={{
              fontFamily: 'monospace',
              fontSize: '0.75rem',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-all',
              mt: 0.5,
            }}
          >
            {JSON.stringify(entry.changes, null, 2)}
          </Box>
        </Box>
      )}
      {entry.integrity_hash && (
        <Box>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {t('logs.table.integrityHash')}
          </Typography>
          <Box
            component="pre"
            sx={{
              fontFamily: 'monospace',
              fontSize: '0.75rem',
              wordBreak: 'break-all',
              mt: 0.5,
            }}
          >
            {entry.integrity_hash}
          </Box>
        </Box>
      )}
    </Box>
  );

  return (
    <Box>
      <Typography variant="h2" component="h1" sx={{ mb: 3 }}>
        {t('logs.title')}
      </Typography>
      <DataTable<LogEntry>
        rows={entries}
        columns={columns}
        getRowId={rowId}
        loading={loading}
        error={error}
        onRetry={load}
        emptyMessage={t('logs.empty')}
        searchPlaceholder={t('logs.filters.searchPlaceholder')}
        filters={filters}
        extraFilters={extraFilters}
        toolbarActions={toolbarActions}
        defaultSort={{ columnId: 'timestamp', dir: 'desc' }}
        paginate
        renderExpanded={renderExpanded}
        isRowExpanded={(e) => expandedRows.has(rowId(e))}
        ariaLabel={t('logs.tableLabel')}
      />
    </Box>
  );
};

export default LogViewer;
